import imagesLoaded from "imagesloaded";
import Masonry from "masonry-layout";
import { IQueryService, QueryService } from "../../services/query-service";
import { ApiService } from "../../services/api-service.js";
import * as RX from "rxjs";
import {
  FilterDefinitionByUrl,
  FilterPanel,
  FilterPanelParameters,
  FilterState,
} from "../filterPanel/filterPanel.component.js";
import { CatchFailure, Failure } from "../../helpers/failure.js";
import {
  MapViewParameters,
  ModuleListMapView,
} from "../moduleListViews/mapView/moduleListMapView.js";
import {
  PaginatedView,
  PaginationParameters as PaginationViewParameters,
} from "../moduleListViews/paginatedView/moduleListPaginatedView.js";
import {
  UGElement,
  RelatedModules,
  ElementWithDataset,
  ElementListWithDataset,
} from "../../models/module.js";
import { getSpinnerHtml } from "../loadingSpinner/spinner.js";
import { MyFavoritesFilter } from "../FavoritesFilter/myFavoritesFilter.js";
import { OtherFavoritesFilter } from "../FavoritesFilter/otherFavoritesFilter";
import { Button } from "../button/button.js";
import { Toast } from "toaster-js";
import { shuffle, toString } from "lodash-es";
import { array, either, option, record } from "fp-ts";
import { constant, flow, pipe, tuple } from "fp-ts/lib/function.js";
import { Option } from "fp-ts/lib/Option";
import {
  LanguageService,
  TranslationTexts,
} from "src/js/services/language-service.js";
import { ModuleFilter } from "../filterPanel/moduleFilter";
import { Accessor, FilterParser } from "../parsers/moduleFilterParser";
import { andThen } from "src/js/logic/functions";
import { warn } from "../../helpers/browserConsole";
// Needed to support for iOS 15.8 - URL.canParse
import "core-js/actual/url/can-parse";
import { IFavoritesService } from "src/js/services/favorites/favorites-interface";
import {
  AppNavigationService,
  INavigationService,
} from "src/js/services/navigation-service";
import { App } from "src/js/app/app";
import {
  ApiFilterPanel,
  ApiSearchView,
  navigationType,
  SearchViewParams,
} from "src/js/models/searchView";
import { UGExhibition } from "src/js/models/exhibition";
import { ModuleListEvent } from "../moduleListViews/moduleListView";
import {
  NavigationPath,
  ViewState,
  ViewStateDetailView,
  ViewStateLocationView,
} from "../parsers/fragmentParser";
import {
  getTemplate,
  setPostTypeForTemplate,
  setTemplateType,
} from "src/js/helpers/element-helpers";
import { initializeSelectElements } from "src/js/helpers/choices-helpers";
import { match } from "ts-adt";
import { fromEquals } from "fp-ts/lib/Eq";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import {
  viewMode,
  ViewMode,
  viewModeAttributesMapping,
  ViewModeParameters,
} from "src/js/models/viewMode";
import { SearchModuleParameters } from "src/js/models/searchModuleParameters";
import { getClicks$ } from "src/js/helpers/rxjs-helpers";
import { renderSearchView, ServiceSet } from "src/js/app/searchviewModule";
import { initializeSwiper } from "src/js/helpers/swiper-helpers";
import { initializePhotoSwipe } from "src/js/helpers/photo-swipe-lightbox";
import { shouldHideElementList } from "src/js/components/searchModule/emptyListVisibility";

const DEFAULT_VIEWMODE: ViewMode = viewMode.grid;
const QUERY_VIEW_MODE_KEY = "view";

function asFilterPanelParameters(
  config: SearchModuleParameters
): FilterPanelParameters {
  return {
    containerElement:
      config.filterPanelElement || Failure("No filter panel container"),
    initialState: {},
    defaultFilter: (x) => true,
  };
}

function asPaginationViewParameters(
  config: SearchModuleParameters
): PaginationViewParameters {
  return {
    elementsContainer: config.elementsContainer,
    pageSize: config.pageSizes,
    paginationContainers: option.getOrElse(() => [] as HTMLElement[])(
      config.paginationContainers
    ),
    queryService: config.queryService,
    apiService: config.apiService,
  };
}
function asMapViewParameters(
  config: SearchModuleParameters
): MapViewParameters {
  return {
    apiService: config.apiService,
    elementsContainer: config.elementsContainer,
    exhibitionId: config.exhibition.id,
    showTooltips: config.tooltipStandmap,
    iconValueAccessor: config.mapIconAccessor,
  };
}

export function fromModuleList(
  modules: ElementListWithDataset,
  configuration: SearchModuleParameters
) {
  init(RX.of(modules), configuration);
}

export function fromDomElement(
  containerElement: HTMLElement,
  searchView: ApiSearchView,
  searchViewParams: SearchViewParams,
  filterPanel: ApiFilterPanel,
  exhibition: UGExhibition,
  languageService: LanguageService,
  queryService: IQueryService,
  apiService: ApiService,
  favoritesService: IFavoritesService,
  navigationServicePage: INavigationService,
  navigationServiceMap: INavigationService
) {
  const configuration = extractSearchModuleConfiguration(
    containerElement,
    searchView,
    searchViewParams,
    filterPanel,
    exhibition,
    languageService,
    apiService,
    queryService,
    favoritesService,
    navigationServicePage,
    navigationServiceMap
  );

  // Get other users favorites if they exits
  const otherFavoritesIds$ = getOtherFavoritesIds$(apiService);

  // get posts
  const posts$: RX.Observable<ElementListWithDataset> = getModules$(
    configuration
  ).pipe(
    RX.combineLatestWith(otherFavoritesIds$),
    RX.map(([posts, otherUsersFavorites]) => ({
      elements: configuration.postTypeFilter.flatMap(
        (postType) => posts[postType].elements
      ),
      relatedModules: posts,
      otherUsersFavorites: otherUsersFavorites,
      templateOptions: configuration.searchViewTemplateOptions,
    }))
  );

  init(posts$, configuration);
}

function init(
  modulesWithDataSet$: RX.Observable<ElementListWithDataset>,
  config: SearchModuleParameters
) {
  config.container.addEventListener("mousedown", (e) => e.stopPropagation()); // fixes issue where modals catches choices mousedown event and closes the modal


  config.elementsContainer.innerHTML = getSpinnerHtml();

  // FilterPanel buttons
  const copyFavoritesLinkButtonEffect$ = getCopyFavoritesButtonEffect$(
    config.favoritesService,
    config.copyFavoritesLinkButton,
    config.sharedFavoritesUrl,
    config.apiService,
    config.languageService.getTranslations()
  );
  const FavoritesFilter = getFavoritesFilter(
    config.queryService,
    config.favoritesService,
    config.myFavoritesButton
  );

  const OtherFavoritesFilter = getOtherFavoritesFilter(
    config.apiService,
    config.otherFavoritesButton
  );

  // Configure ViewMode buttons
  const viewMode$ = getViewMode$(config.viewMode).pipe(
    RX.tap((viewmode) =>
      config.queryService.set(QUERY_VIEW_MODE_KEY, viewMode.toString(viewmode))
    ),
    RX.startWith(config.viewMode.defaultViewMode),
    RX.tap((viewMode) => replaceViewModeClass(config.container, viewMode)),
    RX.tap((viewMode) =>
      setActiveViewModeButton(config.viewMode.ButtonElements, viewMode)
    )
  );

  function tryParseInt(value: string): number | null {
    const num = parseInt(value, 10);
    return isFinite(num) ? num : null;
  }

  const SortShuffleLimitOffset: RX.OperatorFunction<
    UGElement[],
    UGElement[]
  > = (source$) =>
    source$.pipe(
      RX.map((posts) =>
        CatchFailure<UGElement[]>([])(() => {
          // Lav en kopi af posts for at undgå at mutere det originale array
          return posts.slice().sort((a, b) => {
            // Iterer gennem hvert accessor i SortFieldAccessors
            for (const accessor of config.SortFieldAccessors) {
              const aValue = accessor(a).toString().toLowerCase();
              const bValue = accessor(b).toString().toLowerCase();

              const aNum = tryParseInt(aValue);
              const bNum = tryParseInt(bValue);

              if (aNum !== null && bNum !== null) {
                // Sorter numeriske værdier
                const numberComparison = aNum - bNum;
                if (numberComparison !== 0) {
                  return config.sort === "asc"
                    ? numberComparison
                    : -numberComparison;
                }
              } else {
                // Sorter ikke-numeriske værdier
                const stringComparison = aValue.localeCompare(bValue);
                if (stringComparison !== 0) {
                  return config.sort === "asc"
                    ? stringComparison
                    : -stringComparison;
                }
              }
            }
            return 0; // Hvis alle accessors er ens, returner 0 (ingen ændring i sortering)
          });
        })
      ),
      // Anvend offset
      RX.map((posts) => {
        const offset = config.searchViewParams.offset ?? 0;
        return offset > 0 ? posts.slice(offset) : posts;
      }),
      // Anvend limit
      RX.map((posts) => {
        const limit = config.searchViewParams.limit ?? posts.length;
        return limit > 0 ? posts.slice(0, limit) : posts;
      }),
      // Anvend shuffle til sidst, hvis det er aktiveret
      RX.map((posts) => {
        if (config.shuffleList) {
          return shuffle(posts);
        } else {
          return posts;
        }
      })
    );

  // Element count before any filtering, so an empty search result can be told
  // apart from a module that holds nothing at all.
  let moduleHasElements = false;

  const ShowMessageIfEmpty: RX.OperatorFunction<UGElement[], UGElement[]> = (
    source$
  ) =>
    source$.pipe(
      RX.tap(() => {
        const hide = shouldHideElementList({
          moduleHasElements,
          showWhenEmpty: config.hideIfEmpty,
        });

        // Only the element list is hidden — the filter panel stays put so a
        // search with no hits can always be corrected without a page reload.
        config.elementsContainer.classList.toggle("ug-hidden", hide);
        config.container.classList.remove("ug-spinner");
      }),
      RX.map((posts) => posts) // Ensure the posts are passed through
    );

  const ViewModeFilter: RX.OperatorFunction<UGElement[], UGElement[]> = (
    source$
  ) =>
    source$.pipe(
      RX.combineLatestWith(viewMode$),
      RX.map(([posts, viewMode]) => {
        const predicate = pipe(
          viewMode,
          match({
            grid: () => config.elementFilterForPage,
            list: () => config.elementFilterForPage,
            masonry: () => config.elementFilterForPage,
            masonryho: () => config.elementFilterForPage,
            map: () => config.elementFilterForMap,
          })
        );
        return posts.filter(predicate);
      })
    );

  const FilterPanelOperation: RX.OperatorFunction<UGElement[], UGElement[]> = (
    source$
  ) =>
    source$.pipe(
      RX.combineLatestWith(MakePostsFilter(config)),
      RX.map(([posts, postsFilter]) => postsFilter(posts))
    );

  const FavoritesFilterOperation: RX.OperatorFunction<
    UGElement[],
    UGElement[]
  > = (source$) =>
    source$.pipe(
      RX.switchMap((posts: UGElement[]) => {
        return RX.of(posts).pipe(FavoritesFilter, OtherFavoritesFilter);
      })
    );

  const filteredPosts$: RX.Observable<ElementListWithDataset> =
    modulesWithDataSet$.pipe(
      RX.map((moduleSet) => {
        moduleHasElements = moduleSet.elements.length > 0;
        return moduleSet.elements;
      }),
      SortShuffleLimitOffset,
      ViewModeFilter,
      FilterPanelOperation,
      FavoritesFilterOperation,
      ShowMessageIfEmpty,
      RX.combineLatestWith(modulesWithDataSet$),
      RX.map(
        ([elements, dataset]): ElementListWithDataset => ({
          ...dataset,
          elements,
        })
      ),
      RX.shareReplay(1)
    );

  const paginatedView = PaginatedView.MakeView(
    asPaginationViewParameters(config)
  );
  const paginatedRender$ = filteredPosts$.pipe(paginatedView.render$);

  const mapView = ModuleListMapView.MakeView(asMapViewParameters(config));
  const staticMapMarkers$: RX.Observable<UGElement[]> =
    modulesWithDataSet$.pipe(
      RX.map((dataset) => dataset.elements),
      RX.map((posts) => posts.filter(config.elementFilterForMapStatic))
    );
  const mapViewPosts$ = filteredPosts$.pipe(
    RX.combineLatestWith(staticMapMarkers$),
    RX.map(([filteredPosts, staticMarkers]) => {
      return {
        ...filteredPosts,
        elements: array.union(
          fromEquals((a: UGElement, b: UGElement) => a.id == b.id)
        )(staticMarkers)(filteredPosts.elements),
      };
    }),
    RX.tap((x) => x)
  );
  const mapViewRender$ = mapViewPosts$.pipe(mapView.render$);

  const viewEvent$ = viewMode$.pipe(
    RX.switchMap((viewMode) => {
      const event$ = pipe(
        viewMode,
        match({
          grid: () => paginatedView.event$,
          list: () => paginatedView.event$,
          masonry: () => paginatedView.event$,
          masonryho: () => paginatedView.event$,
          map: () => mapView.event$,
        })
      );

      return event$;
    })
  );

  const viewRender$ = viewMode$.pipe(
    RX.switchMap((viewMode) => {
      const render$ = pipe(
        viewMode,
        match({
          grid: () =>
            paginatedRender$.pipe(
              RX.tap((moduleWithDataSet) => {
                SetupAccordion(config.elementsContainer);
              })
            ),
          list: () =>
            paginatedRender$.pipe(
              RX.tap((moduleWithDataSet) => {
                SetupAccordion(config.elementsContainer);
              })
            ),
          masonry: () =>
            paginatedRender$.pipe(
              RX.tap((moduleWithDataset) => {
                if (moduleWithDataset.elements.length > 1) {
                  // Tjek antallet af elementer
                  imagesLoaded(config.elementsContainer, () => {
                    new Masonry(config.elementsContainer, {
                      horizontalOrder: false,
                      itemSelector: `#${config.elementId} .elementcontainer`,
                    });
                  });
                }
              })
            ),
          masonryho: () =>
            paginatedRender$.pipe(
              RX.tap((moduleWithDataset) => {
                if (moduleWithDataset.elements.length > 1) {
                  // Tjek antallet af elementer
                  imagesLoaded(config.elementsContainer, () => {
                    new Masonry(config.elementsContainer, {
                      horizontalOrder: false,
                      itemSelector: `#${config.elementId} .elementcontainer`,
                    });
                  });
                }
              })
            ),
          map: () => mapViewRender$,
        })
      );
      return render$;
    })
  );

  // When all api results are in
  RX.firstValueFrom(modulesWithDataSet$).then((dataset) => {
    pipe(
      config.navigationServicePage.readStoredState(),
      array.findFirst(
        (formState) => formState.formId == config.searchViewFormId.toString()
      ),
      option.map((navigationState) =>
        navigationPathToModuleListEvent(navigationState, dataset)
      ),
      option.getOrElseW(() => {
        // console.log("no navigation state found");
        return [];
      })
    ).forEach((event) => handleSearchViewInteraction(event, dataset, config));
  });

  const moduleModalHandler$ = viewEvent$.pipe(
    RX.withLatestFrom(modulesWithDataSet$),
    RX.tap(([event, postsWithDataSet]) => {
      handleSearchViewInteraction(event, postsWithDataSet, config);
    })
  );

  copyFavoritesLinkButtonEffect$.subscribe();
  moduleModalHandler$.subscribe();
  viewRender$
    .pipe(
      RX.tap((x) => {
        config.onRender(x.elements);
        config.favoritesService.updateDom();
      })
    )
    .subscribe();
}

function SetupAccordion(container: HTMLElement) {
  const accordionHeaders = container.querySelectorAll<HTMLElement>(
    ".ug-accordion-header"
  );

  accordionHeaders.forEach((header) => {
    header.addEventListener("click", (e) => {
      e.preventDefault();

      const contentId = header.getAttribute("aria-controls");
      if (!contentId) return;

      const content = document.getElementById(contentId);
      if (!content) return;

      // Find accordion-containeren
      const accordionContainer = header.closest(".ug-accordion");
      if (!accordionContainer) return;

      const isExpanded = content.classList.contains("ug-active");

      // Luk alle andre åbne accordions i samme container
      const otherContents = accordionContainer.querySelectorAll(
        ".ug-accordion-content"
      );
      otherContents.forEach((otherContent: Element) => {
        const otherContentHTMLElement = otherContent as HTMLElement;
        if (
          otherContentHTMLElement !== content &&
          otherContentHTMLElement.classList.contains("ug-active")
        ) {
          // Animer lukning af andre accordions
          otherContentHTMLElement.classList.remove("ug-active");
          const otherContentInner =
            otherContentHTMLElement.querySelector(".panel-body") ||
            otherContentHTMLElement;
          const otherContentHeight = otherContentInner.scrollHeight;
          otherContentHTMLElement.style.height = `${otherContentHeight}px`;
          otherContentHTMLElement.style.opacity = "1";

          // Trigger reflow
          void otherContentHTMLElement.offsetHeight;

          // Animer til lukket tilstand
          otherContentHTMLElement.style.height = "0px";
          otherContentHTMLElement.style.opacity = "0";

          // Fjern inline-styles efter animationen
          setTimeout(() => {
            otherContentHTMLElement.style.height = "";
            otherContentHTMLElement.style.opacity = "";
          }, 300);
        }
      });

      if (isExpanded) {
        // Luk indholdet med animation
        content.classList.remove("ug-active");
        const contentInner = content.querySelector(".panel-body") || content;
        const contentHeight = contentInner.scrollHeight;

        // Sæt start-højde og opacity
        content.style.height = `${contentHeight}px`;
        content.style.opacity = "1";

        // Trigger reflow
        void content.offsetHeight;

        // Animer til lukket tilstand
        content.style.height = "0px";
        content.style.opacity = "0";

        // Fjern inline-styles efter animationen
        setTimeout(() => {
          content.style.height = "";
          content.style.opacity = "";
        }, 300);
      } else {
        // Åbn indholdet med animation
        content.classList.add("ug-active");
        const contentInner = content.querySelector(".panel-body") || content;
        const contentHeight = contentInner.scrollHeight;

        // Animer højde fra 0 til den målte højde
        content.style.height = "0px";
        content.style.opacity = "0";
        content.style.visibility = "visible";

        // Trigger reflow for at starte animationen
        void content.offsetHeight;

        // Sæt den endelige højde
        content.style.height = `${contentHeight}px`;
        content.style.opacity = "1";

        // Nulstil højde til 'auto' efter animationen er færdig
        setTimeout(() => {
          content.style.height = "auto";
        }, 300);
      }
    });
  });
}

function GetInitialFilterState(
  queryService: IQueryService,
  filterDefinitionsByURL: FilterDefinitionByUrl
): FilterState {
  const queryParams = record.keys(filterDefinitionsByURL);
  const queryValues = pipe(
    queryParams,
    array.map((key) => tuple(key, queryService.getArray(key))),
    record.fromEntries,
    record.compact
  );

  const initialFilterState = FilterPanel.DecodeFilterState(
    filterDefinitionsByURL,
    queryValues
  );
  return initialFilterState;
}

function MakePostsFilter(
  config: SearchModuleParameters
): RX.Observable<(x: UGElement[]) => UGElement[]> {
  if (config.filterPanelElement == false) {
    return RX.of((x) => x);
  }

  const filterDefinitionsByUrl = FilterPanel.ExtractFilterConfig(
    config.filterPanelElement
  );

  const initialFilterState = GetInitialFilterState(
    config.queryService,
    filterDefinitionsByUrl
  );

  const FilterPanelStateEvents$ = FilterPanel.Configure({
    ...asFilterPanelParameters(config),
    initialState: initialFilterState,
  });

  // this is the effect which updates the URL
  const FilterPanelState$ = FilterPanelStateEvents$.pipe(
    RX.tap((event) => {
      // Handle, SET_URL events
      if (event.type != "SET_URL") return;
      pipe(
        event.state,
        FilterPanel.EncodeFilterState,
        record.mapWithIndex((property, values) => {
          if (values.length == 0 || (values.length == 1 && values[0] == "")) {
            config.queryService.remove(property);
          } else {
            config.queryService.setArray(property, values);
          }
        })
      );
    }),
    RX.filter((event) => event.type == "SET_STATE"),
    RX.map((setStateEvent) => setStateEvent.state)
  );

  const postsFilter$ = FilterPanelState$.pipe(
    RX.distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
    RX.map((state) => {
      const filterFunction = FilterPanel.MakeFilterFunction<UGElement>(state);
      return (posts: UGElement[]) => filterFunction(posts);
    })
  );
  // RX.map(FilterPanel.MakeFilterFunction<UGElement>)
  return postsFilter$;
}

function getViewModeFromElementClass(element: HTMLElement): Option<ViewMode> {
  return pipe(
    Array.from(element.classList),
    array.map(viewModeAttributeToViewMode),
    array.compact,
    array.head
  );
}

function getViewModeFromQueryService(
  queryService: IQueryService
): Option<ViewMode> {
  return pipe(
    queryService.get(QUERY_VIEW_MODE_KEY),
    option.flatMap(viewModeAttributeToViewMode)
  );
}

function replaceViewModeClass(element: HTMLElement, viewMode: ViewMode) {
  const viewModeAttributes = viewModeAttributesMapping.map((x) => x.attribute);
  const selectedViewModeAttribute =
    viewModeAttributesMapping.find((x) => x.viewMode == viewMode)?.attribute ??
    Failure(`failed to map viewmode ${viewMode} to attribute`);
  element.classList.remove(...viewModeAttributes);
  element.classList.add(selectedViewModeAttribute);
}

function viewModeAttributeToViewMode(attributeValue: string): Option<ViewMode> {
  return pipe(
    viewModeAttributesMapping,
    array.findFirst((x) => x.attribute == attributeValue),
    option.map((x) => x.viewMode)
  );
}

export function extractSearchModuleConfiguration(
  container: HTMLElement,
  searchView: ApiSearchView,
  searchViewParams: SearchViewParams,
  filterPanel: ApiFilterPanel,
  exhibition: UGExhibition,
  languageService: LanguageService,
  apiService: ApiService,
  queryService: IQueryService,
  favoritesService: IFavoritesService,
  navigationServicePage: INavigationService,
  navigationServiceMap: INavigationService
): SearchModuleParameters {
  const findElement = (query: string) =>
    option.fromNullable(container.querySelector<HTMLElement>(query));

  queryService.setPrefix(searchView.id.toString());

  const compileFilterQuery = (elementFilter: string | null) =>
    pipe(
      option.fromNullable(elementFilter),
      option.map(
        flow(
          FilterParser.parseFilterList,
          either.getOrElseW((parseError) =>
            Failure("Invalid Filter query: " + parseError)
          ),
          ModuleFilter.queryAsPredicate
        )
      ),
      option.getOrElseW(() => (x: any) => true)
    );

  // Brug funktionen med de forskellige inputs
  const elementFilterForPage = compileFilterQuery(searchView.elementFilterPage);
  const elementFilterForMap = compileFilterQuery(searchView.elementFilterMap);
  const elementFilterForMapStatic = compileFilterQuery(
    searchView.elementFilterMapStatic
  );

  const sortFieldAccessors = pipe(
    searchView.elementSortByField,
    FilterParser.parseFieldsAccesor,
    either.getOrElseW((parseError) => {
      console.error("Parse error:", parseError);
      return Failure(parseError);
    }),
    (accessors: NonEmptyArray<Accessor>) => {
      // Returner et array af funktioner, en for hver accessor
      return accessors.map((accessor, index) => {
        return (inputObject: UGElement) => {
          const lookupFunction =
            ModuleFilter.accessorToLookupFunction(accessor);
          let sortValues = lookupFunction(inputObject);
          if (sortValues.length > 1) {
            Failure(
              `Module with ID (${
                inputObject.id
              }) has multiple values (${JSON.stringify(
                sortValues
              )}) for sort key: '${searchView.elementSortByField}'`
            );
          }
          if (sortValues.length === 0) {
            warn(
              `Module with ID (${inputObject.id}) has no values for sort key: '${searchView.elementSortByField}'. Will update in the background and refresh list`
            );
            return ""; // Returner en tom streng eller en anden standardværdi
          }
          const value = sortValues[0].toString();
          return value; // Returner den første værdi som en streng
        };
      });
    }
  );

  const mapIconAccessor = pipe(
    option.fromNullable(searchView.mapIconValueQuery),
    option.map(
      flow(
        FilterParser.parseFieldAccesor,
        either.getOrElseW((parseError) => Failure(parseError)), // handle attribute query string being invalid format

        ModuleFilter.accessorToLookupFunction,
        andThen(
          flow(
            array.head,
            option.getOrElseW(() => ""),
            toString
          )
        )
      )
    ),
    option.getOrElseW(() => constant("")) // handle no data-attribute set
  );

  const myFavoritesButton = findElement(
    ".filter_panel .buttons .btn.visfavoritter"
  );

  const otherFavoritesButton = findElement(
    ".filter_panel .buttons .btn.vis_anden_brugers_favoritter"
  );

  const copyFavoritesLinkButton =
    (container.querySelector(
      ".filter_panel .buttons .btn.delfavoritter"
    ) as HTMLElement | null) ?? false;

  const goToFavoritesLinkButton =
    (container.querySelector(
      ".filter_panel .buttons .btn.link_favoritside"
    ) as HTMLElement | null) ?? false;

  const filterPanelElement =
    (container.querySelector(".filter_panel") as HTMLElement | null) ?? false;

  const elementsContainer: HTMLElement =
    container.querySelector(".elements") ??
    Failure("Missing .elements child element");

  const paginationContainers: Option<HTMLElement[]> =
    Array.from(container.querySelectorAll(".controls-pagination")).length > 0
      ? option.some(
          Array.from(container.querySelectorAll(".controls-pagination"))
        )
      : option.none;

  // if (paginationContainers.length == 0) {
  //   const bottomPagination = document.createElement("div");
  //   bottomPagination.classList.add("controls-pagination");
  //   elementsContainer.after(bottomPagination);
  //   paginationContainers.push(bottomPagination);
  // }

  return {
    clientSpecificClass: searchView.class,
    searchViewTemplateOptions: searchView.templateOptions,
    searchViewTemplate: filterPanel.filterPanelHtml,
    searchViewFormId: searchView.id,
    searchViewParams: searchViewParams,

    postTypeFilter: searchView.postTypeFilter, // the customPostType to show, also known as the moduleType
    elementFilterForPage: elementFilterForPage,
    elementFilterForMap: elementFilterForMap,
    elementFilterForMapStatic: elementFilterForMapStatic,
    SortFieldAccessors: sortFieldAccessors,
    sort: searchView.elementSort,
    hideIfEmpty: searchView.hideIfEmpty, // Hide entire search module element if initial list of modules is empty

    exhibition: exhibition,
    sharedFavoritesUrl: pipe(
      searchView.sharedFavoritesUrl,
      option.fromNullable,
      option.map((x) => new URL(x))
    ),

    elementClickBehaviorPage: searchView.navigationTypePage,
    elementClickBehaviorMap: searchView.navigationTypeMap,

    extraParametersUrl: false,
    viewMode: extractParametersViewMode(queryService, container),
    viewModeNavigation: viewMode.fromString(searchView.viewModeNavigation),

    pageSizes: searchView.pageSizes,
    shuffleList: searchView.shuffleList,

    container: container,
    myFavoritesButton: myFavoritesButton,
    otherFavoritesButton: otherFavoritesButton,
    copyFavoritesLinkButton: copyFavoritesLinkButton,
    filterPanelElement: filterPanelElement,
    elementsContainer: elementsContainer,
    paginationContainers: paginationContainers,

    mapIconAccessor,
    tooltipStandmap: searchView.tooltipStandmap,

    languageService: languageService,
    apiService: apiService,
    queryService: queryService,
    favoritesService: favoritesService,
    navigationServicePage: navigationServicePage,
    navigationServiceMap: navigationServiceMap,

    onRender: (x) => {},

    elementId: searchView.elementId,
  };
}

function extractParametersViewMode(
  queryService: IQueryService,
  container: HTMLElement
): ViewModeParameters {
  const viewMode = pipe(
    [
      getViewModeFromQueryService(queryService),
      getViewModeFromElementClass(container),
    ],
    array.compact,
    array.head,
    option.getOrElse(constant(DEFAULT_VIEWMODE))
  );

  return {
    defaultViewMode: viewMode,
    ButtonElements: Array.from(
      container.querySelectorAll(".views > .show-type")
    ),
  };
}

/** @returns {RX.Observable<Post[]>} */
function getModules$(
  moduleConfig: SearchModuleParameters
): RX.Observable<RelatedModules> {
  return App.getExhibitionFromId(moduleConfig.exhibition.id).pipe(
    RX.map((exhibition) => exhibition.modules)
  );
}

function getFavoritesFilter(
  queryService: IQueryService,
  favoritesService: IFavoritesService,
  button: Option<HTMLElement>
): RX.OperatorFunction<UGElement[], UGElement[]> {
  return pipe(
    button,
    option.match(
      () => RX.identity,
      (button) =>
        MyFavoritesFilter.FromButton(
          button,
          favoritesService.get$(),
          queryService
        )
    )
  );
}

function getOtherFavoritesFilter(
  apiService: ApiService,
  button: Option<HTMLElement>
): RX.OperatorFunction<UGElement[], UGElement[]> {
  const otherUsersFavorites$ = getOtherFavoritesIds$(apiService);

  return pipe(
    button,
    option.match(
      () => RX.identity,
      (button) => OtherFavoritesFilter.FromButton(button, otherUsersFavorites$)
    )
  );
}

function getOtherFavoritesIds$(
  apiService: ApiService
): RX.Observable<number[]> {
  const mayberOtherFavoritesFromUserId = pipe(
    new QueryService().get("bruger"),
    option.map(apiService.getUserFavorites)
  );
  const mayberOtherFavoritesFromQueryList = pipe(
    new QueryService().getArray("favoritter"),
    option.map(
      flow(
        array.map(flow(parseInt, option.fromNullable)),
        array.compact,
        (arr) => RX.of(arr)
      )
    )
  );

  const otherUsersFavoritesIds$ = pipe(
    mayberOtherFavoritesFromUserId,
    option.orElse(() => mayberOtherFavoritesFromQueryList),
    option.getOrElseW(() => RX.of([]))
  );

  return otherUsersFavoritesIds$;
}

function getCopyFavoritesButtonEffect$(
  favoritesService: IFavoritesService,
  button: HTMLElement | false,
  sharedFavoritesUrl: Option<URL>,
  apiService: ApiService,
  texts: TranslationTexts
): RX.Observable<any> {
  return button === false
    ? RX.of()
    : Button.onClick$(button).pipe(
        RX.withLatestFrom(favoritesService.get$(), apiService.getUserId()),
        RX.tap(([_, favorites, maybeUserId]) =>
          onClickShareLink(favorites, sharedFavoritesUrl, maybeUserId, texts)
        )
      );
}

function getViewMode$(config: ViewModeParameters): RX.Observable<ViewMode> {
  return getViewModeButtonClicks$(config.ButtonElements).pipe(
    RX.distinctUntilChanged()
  );
}

function getViewModeButtonClicks$(
  buttons: HTMLElement[]
): RX.Observable<ViewMode> {
  return RX.fromEvent(buttons, "click").pipe(
    RX.map((clickEvent) =>
      option.fromNullable(
        (clickEvent.currentTarget as HTMLElement).dataset.viewMode
      )
    ),
    RX.filter(option.isSome),
    RX.map((x) => x.value),
    RX.map(viewModeAttributeToViewMode),
    RX.map(
      option.getOrElseW(() => Failure("Invalid view mode attribute value"))
    ),
    RX.shareReplay(1)
  );
}

function setActiveViewModeButton(buttons: HTMLElement[], viewMode: ViewMode) {
  const viewModeAttribute =
    viewModeAttributesMapping.find((x) => x.viewMode == viewMode)?.attribute ??
    Failure(`Missing viewModeAttribute mapping for viewmode: ${viewMode}`);

  buttons.forEach((button) => {
    if (button.classList.contains(viewModeAttribute)) {
      button.classList.add("active");
    } else {
      button.classList.remove("active");
    }
  });
}

function onClickShareLink(
  favorites: number[],
  sharedFavoritesUrl: Option<URL>,
  userId: Option<string>,
  texts: TranslationTexts
) {
  if (option.isNone(sharedFavoritesUrl)) {
    return Failure("Missing data-favorites-url");
  }

  if (favorites.length == 0) {
    new Toast(texts.toast.linkCopyEmpty, Toast.TYPE_INFO, 3000);
    return;
  }

  const resultUrl = new URL(sharedFavoritesUrl.value);

  // if is search-module has data-user-id=...
  if (option.isSome(userId)) {
    // copy ${favorites-url}?bruger=${user-id}
    resultUrl.searchParams.append("bruger", userId.value);
  } else {
    // copy ${favorites-url}?favoritter=${favorites.join(',')}
    resultUrl.searchParams.append("favoritter", favorites.join(","));
  }

  const resultString = resultUrl.toString();

  copyToClipboard(resultString).then((success) => {
    if (success) {
      new Toast(texts.toast.linkCopy, Toast.TYPE_INFO, 3000);
    } else {
      new Toast(texts.toast.linkCopyError, Toast.TYPE_INFO, 3000);
    }
  });
}

/**
 * @param text Text to copy to clipboard
 * @returns true if successfull, false on error
 */
async function copyToClipboard(text: string): Promise<Boolean> {
  try {
    const type = "text/plain";
    const blob = new Blob([text], { type });
    const data = [new ClipboardItem({ [type]: blob })];

    return navigator.clipboard.write(data).then(
      () => {
        return true;
      },
      () => {
        return false;
      }
    );
  } catch {
    return false;
  }
}

function navigationPathToModuleListEvent(
  navigationPath: NavigationPath,
  dataset: ElementListWithDataset
): ModuleListEvent[] {
  function ViewStateToModuleListEvent(
    view: ViewState
  ): Option<ModuleListEvent> {
    if (view.type == "DetailView") {
      return ViewStateDetailViewToModuleListEvent(view);
    } else {
      return ViewStateLocationViewToModuleListEvent(view);
    }
  }

  function ViewStateLocationViewToModuleListEvent(
    view: ViewStateLocationView
  ): Option<ModuleListEvent> {
    return option.none;
    // return pipe(
    // dataset.relatedModules,
    // record.lookup(view.locationSlug),
    // option.flatMap((module) =>
    //   pipe(
    //     module.elements,
    //     array.findFirst((element) => element.slug == view.elementSlug),
    //     option.map((element) => element.id)
    //   )
    // ),
    //   option.map((elementId) => ({
    //     type: "clickSingle",
    //     moduleId: elementId,
    //     target: null,
    //   }))
    // );
  }
  function ViewStateDetailViewToModuleListEvent(
    view: ViewStateDetailView
  ): Option<ModuleListEvent> {
    return pipe(
      dataset.relatedModules,
      record.lookup(view.moduleSlug),
      option.flatMap((module) =>
        pipe(
          module.elements,
          array.findFirst((element) => element.slug == view.elementSlug),
          option.map((element) => element.id)
        )
      ),
      option.map((elementId) => ({
        type: "clickSingle",
        moduleId: elementId,
        target: null,
      }))
    );
  }

  return pipe(
    navigationPath.views,
    array.map(ViewStateToModuleListEvent),
    array.compact
  );
}
function handleSearchViewInteraction(
  event: ModuleListEvent,
  postsWithDataSet: ElementListWithDataset,
  config: SearchModuleParameters
) {
  // Find det aktive element i config.viewMode.ButtonElements
  const activeButton = config.viewMode.ButtonElements.find((button) =>
    button.classList.contains("active")
  );

  // Bestem, hvilken clickBehavior der skal bruges baseret på det aktive element
  const clickBehavior =
    activeButton && activeButton.classList.contains("map")
      ? config.elementClickBehaviorMap
      : config.elementClickBehaviorPage;

  const navigationService =
    activeButton && activeButton.classList.contains("map")
      ? config.navigationServiceMap
      : config.navigationServicePage;

  switch (event.type) {
    case "clickSingle":
      const post =
        postsWithDataSet.elements.find((post) => post.id == event.moduleId) ??
        Failure(`Couldn't find module with id: ${event.moduleId}`);
      switch (clickBehavior) {
        case "none":
          break;
        case "app": {
          const postWithDataset: ElementWithDataset = {
            element: post,
            relatedModules: postsWithDataSet.relatedModules,
            otherUsersFavorites: postsWithDataSet.otherUsersFavorites,
            templateOptions: config.searchViewTemplateOptions,
          };
          const templatePostType = setPostTypeForTemplate(post);

          const templates =
            postWithDataset.relatedModules[templatePostType].templates;

          const template = getTemplate(
            postWithDataset.element,
            templates,
            "detail"
          );

          navigationService.openItem(
            template(postWithDataset),
            {
              name: post.title,
              slug: `${post.postType}/${post.slug}`,
            },
            config.clientSpecificClass
          );
          break;
        }
        case "modal": {
          const postWithDataset: ElementWithDataset = {
            element: post,
            relatedModules: postsWithDataSet.relatedModules,
            otherUsersFavorites: postsWithDataSet.otherUsersFavorites,
            templateOptions: config.searchViewTemplateOptions,
          };
          const templatePostType = setPostTypeForTemplate(post);

          const templates =
            postWithDataset.relatedModules[templatePostType].templates;
          const template = getTemplate(
            postWithDataset.element,
            templates,
            "summary"
          );

          navigationService.openItem(
            template(postWithDataset),
            {
              name: post.title,
              slug: `${post.postType}/${post.slug}`,
            },
            config.clientSpecificClass
          );

          config.favoritesService.updateDom();
          break;
        }
        case "redirect":
          // Brug getClicks$ til at håndtere klik på elementer med data-href
          const linkClick$ = getClicks$(
            "[data-href]",
            navigationService.getContent()
          ).pipe(
            RX.take(1), // Afmeld efter den første emission
            RX.map((element) => (element as HTMLElement).dataset.href),
            RX.filter((href): href is string => href !== undefined)
          );

          const subscription = linkClick$.subscribe((href) => {
            window.location.href = href;
          });
      }

      const container = navigationService.getContent();

      getClicks$(
        ".open_embedded_interactive_elements_view_modal",
        container
      ).subscribe((clickEvent) => {
        const clickedElement = clickEvent as HTMLElement;
        handleOpenEmbeddedInteractiveElementsViewInModal(
          clickedElement,
          config,
          navigationService,
          postsWithDataSet,
          clickBehavior
        );
      });

      initializeInlineSearchView(container, config, clickBehavior);

      config.favoritesService.updateDom();
      initializeSwiper();
      initializePhotoSwipe();

      break;
    case "clickMultiple":
      const modulesPost = event.moduleIds
        .map((id) => postsWithDataSet.elements.find((post) => post.id == id))
        .filter((x) => x !== undefined) as UGElement[];
      const urlIdentity = modulesPost.map((post) => post.id).join("-");
      const clickedModuleDataset: ElementListWithDataset = {
        ...postsWithDataSet,
        elements: modulesPost,
      };
      // const modal = ModalComponent.openModalWithUrlState(
      //   config.searchViewTemplate,
      //   urlIdentity
      // );
      event.target;
      const closeButton =
        '<div class="nested"><div class="modal__buttons_container"><div class="ug_knap right"><a class="modal_close modal__btn">Luk</a></div></div></div>';
      const searchViewTemplate = config.searchViewTemplate + closeButton;

      navigationService.openItem(
        searchViewTemplate,
        {
          name: event.locationId ? event.locationId : "unknownLocation",
          slug: "location/" + (event.locationId ? event.locationId : "_"),
        },
        config.clientSpecificClass
      );
      const uninitializedSearchViewElement =
        navigationService
          .getContent()
          .querySelector<HTMLElement>(".interactive-element-view") ??
        Failure("missing element");

      const newConfig = { ...config };
      newConfig.container = uninitializedSearchViewElement;
      newConfig.elementsContainer =
        newConfig.container.querySelector(".elements") ??
        Failure("missing elements container");
      newConfig.filterPanelElement =
        (newConfig.container.querySelector(
          ".filter_panel"
        ) as HTMLElement | null) ?? false;
      newConfig.myFavoritesButton = option.fromNullable(
        newConfig.container.querySelector<HTMLElement>(
          ".filter_panel .buttons .btn.visfavoritter"
        )
      );
      newConfig.otherFavoritesButton = option.fromNullable(
        newConfig.container.querySelector<HTMLElement>(
          ".filter_panel .buttons .btn.vis_anden_brugers_favoritter"
        )
      );
      newConfig.viewMode = extractParametersViewMode(
        newConfig.queryService,
        newConfig.container
      );
      newConfig.viewMode.defaultViewMode = config.viewModeNavigation;
      newConfig.elementFilterForPage = (x) => true;
      newConfig.elementFilterForMap = (x) => true;
      newConfig.elementFilterForMapStatic = (x) => true;

      newConfig.elementClickBehaviorPage = config.elementClickBehaviorMap;
      newConfig.navigationServicePage = config.navigationServiceMap;

      fromModuleList(clickedModuleDataset, newConfig);
      initializeSelectElements(config.languageService);
      break;
  }

  function initializeInlineSearchView(
    container: HTMLElement,
    config: SearchModuleParameters,
    clickBehavior: navigationType
  ) {
    const inlineSearchViewsElements = container.querySelectorAll(".ug-app");
    const inlineSearchViewContainers = Array.from(
      inlineSearchViewsElements
    ) as HTMLElement[];

    inlineSearchViewContainers.forEach((searchViewElement: HTMLElement) => {
      const searchviewId = searchViewElement.getAttribute("data-ug-id");
      const filterQueryFromContext =
        searchViewElement.getAttribute("data-filter-query");

      if (searchviewId) {
        const searchViewObservable =
          config.apiService.getSearchView(searchviewId);
        const serviceSet: ServiceSet = {
          languageService: config.languageService,
          apiService: config.apiService,
          queryService: config.queryService,
          favoritesService: config.favoritesService,
        };

        searchViewObservable.subscribe((searchView: ApiSearchView) => {
          if (filterQueryFromContext) {
            if (searchView.elementFilterPage) {
              searchView.elementFilterPage += `#${filterQueryFromContext}`;
            } else {
              searchView.elementFilterPage = filterQueryFromContext;
            }
            if (searchView.elementFilterMap) {
              searchView.elementFilterMap += `#${filterQueryFromContext}`;
            } else {
              searchView.elementFilterMap = filterQueryFromContext;
            }
          }

          // Move the switch statement outside the if block
          switch (clickBehavior) {
            case "redirect":
            case "app": {
              searchView.navigationTypePage = config.elementClickBehaviorPage;
              searchView.navigationTypeMap = config.elementClickBehaviorMap;
              break;
            }
            case "modal": {
              config.navigationServicePage = config.navigationServiceMap;
              config.elementClickBehaviorPage = config.elementClickBehaviorMap;
              searchView.navigationTypePage = "modal";
              searchView.navigationTypeMap = "modal";
              break;
            }
          }

          const filterPanelObservable = config.apiService.getFilterPanel(
            config.exhibition.id,
            searchView.id
          );
          filterPanelObservable.subscribe((filterPanel: ApiFilterPanel) => {
            searchViewElement.innerHTML =
              (filterPanel.filterPanelExhibitionCss || "") +
              (filterPanel.filterPanelSearchViewCss || "") +
              (filterPanel.filterPanelHtml || "");
            renderSearchView(
              config.exhibition,
              searchViewElement,
              searchView,
              filterPanel,
              config.searchViewParams,
              config.navigationServicePage,
              config.navigationServiceMap,
              serviceSet
            );
          });
        });
      }
    });

    // Add event listeners for elements with data-scroll-target attribute
    const scrollElements = container.querySelectorAll("[data-scroll-target]");
    scrollElements.forEach((element: Element) => {
      element.addEventListener("click", function (event) {
        event.preventDefault();
        const targetId = (element as HTMLElement).getAttribute(
          "data-scroll-target"
        );
        if (targetId) {
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth" });
          }
        }
      });
    });
  }

  function handleOpenEmbeddedInteractiveElementsViewInModal(
    clickedElement: HTMLElement,
    config: SearchModuleParameters,
    navigationService: INavigationService,
    postsWithDataSet: ElementListWithDataset,
    clickBehavior: navigationType
  ) {
    const searchviewIdFromButton = clickedElement.getAttribute("data-ug-id");
    const filterQueryFromContext =
      clickedElement.getAttribute("data-filter-query");

    if (searchviewIdFromButton) {
      const serviceSet: ServiceSet = {
        languageService: config.languageService,
        apiService: config.apiService,
        queryService: config.queryService,
        favoritesService: config.favoritesService,
      };

      config.apiService
        .getSearchView(searchviewIdFromButton)
        .pipe(
          RX.switchMap((searchView: ApiSearchView) => {
            return config.apiService
              .getFilterPanel(config.exhibition.id, searchView.id)
              .pipe(
                RX.switchMap((filterPanel) => {
                  const closeButton =
                    '<div class="nested"><div class="modal__buttons_container"><div class="ug_knap right"><a class="modal_close modal__btn">Luk</a></div></div></div>';
                  filterPanel.filterPanelHtml =
                    filterPanel.filterPanelSearchViewCss +
                    filterPanel.filterPanelHtml +
                    closeButton;
                  filterPanel.filterPanelExhibitionCss = "";

                  const displayNames = searchView.postTypeFilter
                    .map((postType) => {
                      const module = postsWithDataSet.relatedModules[postType];
                      return module ? module.displayNames : null;
                    })
                    .filter(
                      (displayName): displayName is string =>
                        displayName !== null
                    );

                  const slugs = searchView.postTypeFilter
                    .map((postType) => {
                      const module = postsWithDataSet.relatedModules[postType];
                      return module ? module.slug : null;
                    })
                    .filter((slug): slug is string => slug !== null);

                  navigationService.openItem(
                    filterPanel.filterPanelHtml,
                    {
                      name: displayNames.join(","),
                      slug: slugs.join("-"),
                    },
                    config.clientSpecificClass
                  );

                  let container: HTMLElement = document.createElement("div");
                  switch (clickBehavior) {
                    case "redirect":
                    case "app": {
                      container = config.navigationServicePage.getContent();
                      searchView.navigationTypePage =
                        config.elementClickBehaviorPage;
                      searchView.navigationTypeMap =
                        config.elementClickBehaviorMap;
                      break;
                    }
                    case "modal": {
                      container = config.navigationServiceMap.getContent();
                      config.navigationServicePage =
                        config.navigationServiceMap;
                      config.elementClickBehaviorPage =
                        config.elementClickBehaviorMap;
                      searchView.navigationTypePage = "modal";
                      searchView.navigationTypeMap = "modal";
                      break;
                    }
                  }

                  if (filterQueryFromContext) {
                    if (searchView.elementFilterPage) {
                      searchView.elementFilterPage += `#${filterQueryFromContext}`;
                    } else {
                      searchView.elementFilterPage = filterQueryFromContext;
                    }
                    if (searchView.elementFilterMap) {
                      searchView.elementFilterMap += `#${filterQueryFromContext}`;
                    } else {
                      searchView.elementFilterMap = filterQueryFromContext;
                    }
                  }

                  searchView.navigationTypePage =
                    config.elementClickBehaviorPage;
                  searchView.navigationTypeMap = config.elementClickBehaviorMap;

                  renderSearchView(
                    config.exhibition,
                    container,
                    searchView,
                    filterPanel,
                    config.searchViewParams,
                    config.navigationServicePage,
                    config.navigationServiceMap,
                    serviceSet
                  );

                  return RX.of(null); // Afslut observablen
                })
              );
          })
        )
        .subscribe();
    }
  }
}
